/**
 * Luma-matte compositor. ONLY used when VITE_TRANSITION_MODE=luma.
 *
 * OPEN ITEM: Super Objective have not yet confirmed the transition MP4 spec. The
 * likely delivery is a single H.264 file with colour on the left half and a luma
 * matte on the right, which cannot be composited over the camera view without
 * turning luma into alpha per-pixel. In 2D canvas that means getImageData every
 * frame — far too slow for the kiosk board — so this does it in a one-shader
 * WebGL pass instead.
 *
 * If they deliver a fully opaque wipe, none of this is needed: set
 * VITE_TRANSITION_MODE=opaque (the default) and this file can be deleted along
 * with the flag.
 */

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = vec2((a_pos.x + 1.0) * 0.5, 1.0 - (a_pos.y + 1.0) * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

// Left half: colour. Right half: matte. Rec.709 luma as alpha, premultiplied.
const FRAG = `
precision mediump float;
uniform sampler2D u_tex;
varying vec2 v_uv;
void main() {
  vec2 colorUv = vec2(v_uv.x * 0.5, v_uv.y);
  vec2 matteUv = vec2(v_uv.x * 0.5 + 0.5, v_uv.y);
  vec3 color = texture2D(u_tex, colorUv).rgb;
  vec3 matte = texture2D(u_tex, matteUv).rgb;
  float alpha = clamp(dot(matte, vec3(0.2126, 0.7152, 0.0722)), 0.0, 1.0);
  gl_FragColor = vec4(color * alpha, alpha);
}`;

export interface LumaCompositor {
  /** Draws one frame of the source video. Cheap to call from rAF. */
  render(video: HTMLVideoElement): void;
  destroy(): void;
}

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('WebGL: createShader failed');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unknown';
    gl.deleteShader(shader);
    throw new Error(`WebGL shader compile failed: ${log}`);
  }
  return shader;
}

/** Throws if WebGL is unavailable — callers fall back to the opaque path. */
export function createLumaCompositor(canvas: HTMLCanvasElement): LumaCompositor {
  /**
   * `preserveDrawingBuffer` is load-bearing, not a default worth trimming.
   *
   * This canvas is drawn twice: the browser composites it to the screen, and
   * LogoCompositor does its own `drawImage(lumaCanvas)` from a *separate*
   * requestAnimationFrame to burn the transition into the recorded clip. With
   * the default `false` the drawing buffer is cleared as soon as the browser
   * has composited, so whichever loop runs second samples an empty buffer —
   * which is why the fog was visible live and completely absent from the
   * take-home video.
   *
   * It costs some driver-side optimisation. That is the price of the fog being
   * in the thing people actually share.
   */
  const contextAttributes: WebGLContextAttributes = {
    alpha: true,
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
  };
  const gl = (canvas.getContext('webgl', contextAttributes) ??
    canvas.getContext('experimental-webgl', contextAttributes)) as WebGLRenderingContext | null;
  if (!gl) throw new Error('WebGL unavailable for luma transition');

  const program = gl.createProgram();
  if (!program) throw new Error('WebGL: createProgram failed');
  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`WebGL link failed: ${gl.getProgramInfoLog(program) ?? 'unknown'}`);
  }
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW,
  );
  const posLoc = gl.getAttribLocation(program, 'a_pos');
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.uniform1i(gl.getUniformLocation(program, 'u_tex'), 0);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  let destroyed = false;

  return {
    render(video) {
      if (destroyed || video.readyState < 2) return;
      // Half width: the source is side-by-side, the output is one panel.
      const w = Math.max(1, Math.floor(video.videoWidth / 2));
      const h = Math.max(1, video.videoHeight);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    },
    destroy() {
      destroyed = true;
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    },
  };
}
